import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiMapPin, FiThermometer, FiTrendingUp } from 'react-icons/fi';
import { oceanDataService } from '../services/oceanDataService';
import LoadingSpinner from './LoadingSpinner';

const CityDetails = () => {
  const { cityId } = useParams();
  const [city, setCity] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCityDetails();
  }, [cityId]);

  const loadCityDetails = async () => {
    setLoading(true);
    try {
      const [cityData, historical] = await Promise.all([
        oceanDataService.getCityDetails(cityId),
        oceanDataService.getHistoricalData(cityId)
      ]);
      
      setCity(cityData);
      setHistoricalData(historical);
    } catch (error) {
      console.error('Failed to load city details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChartOptions = () => {
    const dates = historicalData.map(d => new Date(d.timestamp).toLocaleDateString('en-PH'));
    const temperatures = historicalData.map(d => d.temperature);

    return {
      title: {
        text: '7-Day Temperature Trend',
        textStyle: { fontSize: 16, fontWeight: 'bold' }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const point = params[0];
          return `${point.name}<br/>Temperature: ${point.value}°C`;
        }
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { rotate: 45 }
      },
      yAxis: {
        type: 'value',
        name: 'Temperature (°C)',
        min: 26,
        max: 32,
        axisLine: { show: true },
        splitLine: { show: true }
      },
      series: [{
        data: temperatures,
        type: 'line',
        smooth: true,
        lineStyle: { width: 3, color: '#3B82F6' },
        itemStyle: { color: '#3B82F6' },
        areaStyle: { 
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
              { offset: 1, color: 'rgba(59, 130, 246, 0.05)' }
            ]
          }
        },
        markLine: {
          data: [
            { yAxis: 29, name: 'Watch Threshold', lineStyle: { color: '#F59E0B', type: 'dashed' } },
            { yAxis: 30.5, name: 'Alert Threshold', lineStyle: { color: '#EF4444', type: 'dashed' } }
          ],
          label: { position: 'end' }
        }
      }],
      grid: { bottom: 80, top: 60, left: 60, right: 20 }
    };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!city) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">City not found</p>
        <Link to="/" className="text-blue-600 hover:underline">Return to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            <SafeIcon icon={FiArrowLeft} className="text-xl" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiMapPin} className="text-blue-600" />
              {city.name}
            </h1>
            <p className="text-gray-600">{city.region} • {city.coordinates}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiThermometer} className="text-2xl text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {city.temperature ? `${city.temperature.toFixed(1)}°C` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Current Temperature</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiTrendingUp} className="text-2xl text-gray-600" />
              <div>
                <div className="text-2xl font-bold text-gray-700">
                  {city.waveHeight ? `${city.waveHeight.toFixed(1)}m` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Wave Height</div>
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-4 ${
            city.alertLevel === 'alert' ? 'bg-red-50' :
            city.alertLevel === 'watch' ? 'bg-yellow-50' : 'bg-green-50'
          }`}>
            <div className="text-center">
              <div className="text-3xl mb-2">
                {city.alertLevel === 'alert' ? '🔴' :
                 city.alertLevel === 'watch' ? '🟡' : '🟢'}
              </div>
              <div className="font-semibold text-gray-700 capitalize">
                {city.alertLevel} Status
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Chart */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <ReactECharts option={getChartOptions()} style={{ height: '400px' }} />
      </div>

      {/* Alert Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Coral Bleaching Alert Thresholds</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg">
            <span className="text-2xl">🟢</span>
            <div>
              <div className="font-semibold text-green-800">Normal (&lt; 29°C)</div>
              <div className="text-sm text-green-600">Optimal conditions for coral health</div>
            </div>
          </div>
          <div className="flex items-center gap-4 p-3 bg-yellow-50 rounded-lg">
            <span className="text-2xl">🟡</span>
            <div>
              <div className="font-semibold text-yellow-800">Watch (29-30.5°C)</div>
              <div className="text-sm text-yellow-600">Elevated temperatures, monitor closely</div>
            </div>
          </div>
          <div className="flex items-center gap-4 p-3 bg-red-50 rounded-lg">
            <span className="text-2xl">🔴</span>
            <div>
              <div className="font-semibold text-red-800">Alert (&gt; 30.5°C)</div>
              <div className="text-sm text-red-600">High bleaching risk, immediate attention needed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Location Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Location Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Coordinates:</span>
            <span className="ml-2 text-gray-600">{city.coordinates}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Region:</span>
            <span className="ml-2 text-gray-600">{city.region}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Last Reading:</span>
            <span className="ml-2 text-gray-600">
              {city.lastReading ? new Date(city.lastReading).toLocaleString('en-PH') : 'N/A'}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Data Source:</span>
            <span className="ml-2 text-gray-600">NOAA Coral Reef Watch</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityDetails;